import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Check, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import api, { formatPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Loader } from "@/components/Loader";

export default function Builder() {
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [picks, setPicks] = useState({});

  useEffect(() => {
    api
      .get("/lunchbox/options")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load the box builder."))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader label="Opening the lunchbox…" />;
  if (!data) return null;

  const { groups, price_cents, meal } = data;
  const allPicked = groups.every((g) => picks[g.key]);
  const pickedCount = groups.filter((g) => picks[g.key]).length;

  const addToCart = () => {
    if (!allPicked) {
      toast.error("Please pick one from each group.");
      return;
    }
    const config = {};
    groups.forEach((g) => (config[g.key] = picks[g.key]));
    addItem({ meal_id: "meal-custom-box", name: "Build Your Own Box", image_url: meal.image_url, price_cents }, 1, config);
    toast.success("Custom box added to cart! 🎁");
    navigate("/cart");
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display font-bold text-3xl sm:text-4xl text-slate-800 tracking-tight flex items-center gap-3">
          <Box className="text-amber-500" /> Build Your Own Box
        </h1>
        <p className="text-slate-500 mt-1">
          One pick per group — a fresh box for a flat <span className="font-bold text-emerald-600">{formatPrice(price_cents)}</span>.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          {groups.map((g, gi) => (
            <div key={g.key} data-testid={`builder-group-${g.key}`} className="bg-white rounded-3xl border-2 border-emerald-100/80 p-5">
              <div className="flex items-center gap-3 mb-4">
                <span className="h-8 w-8 rounded-xl bg-emerald-100 text-emerald-600 font-display font-bold flex items-center justify-center text-sm">
                  {gi + 1}
                </span>
                <h3 className="font-display font-bold text-lg text-slate-800">{g.label}</h3>
                {picks[g.key] && <Check size={18} className="text-emerald-500 ml-auto" />}
              </div>
              <div className="flex flex-wrap gap-2.5">
                {g.options.map((opt) => {
                  const active = picks[g.key] === opt;
                  return (
                    <button
                      key={opt}
                      data-testid={`builder-option-${g.key}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      onClick={() => setPicks((p) => ({ ...p, [g.key]: opt }))}
                      className={`px-4 py-2.5 rounded-2xl font-semibold text-sm border-2 transition-all ${
                        active
                          ? "bg-emerald-500 text-white border-emerald-500 shadow-md shadow-emerald-200"
                          : "bg-white text-slate-700 border-slate-200 hover:border-emerald-300"
                      }`}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Live preview */}
        <div className="space-y-4">
          <div className="bg-white rounded-3xl border-2 border-amber-100 p-6 sticky top-20">
            <img src={meal.image_url} alt="Custom box" className="w-full h-36 object-cover rounded-2xl mb-4" />
            <h2 className="font-display font-bold text-xl text-slate-800 mb-3">Your Box 🎁</h2>
            <div className="space-y-2">
              {groups.map((g) => (
                <div key={g.key} className="flex items-center justify-between text-sm" data-testid={`builder-preview-${g.key}`}>
                  <span className="text-slate-500 font-medium">{g.label}</span>
                  <span className={`font-semibold ${picks[g.key] ? "text-slate-800" : "text-slate-300"}`}>
                    {picks[g.key] || "—"}
                  </span>
                </div>
              ))}
            </div>
            <div className="h-px bg-slate-200 my-4" />
            <div className="flex items-center justify-between mb-4">
              <span className="font-display font-bold text-slate-800">{pickedCount}/{groups.length} picked</span>
              <span className="font-display font-bold text-2xl text-emerald-600">{formatPrice(price_cents)}</span>
            </div>
            <button
              data-testid="builder-add-to-cart"
              onClick={addToCart}
              disabled={!allPicked}
              className="w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-display font-bold rounded-2xl py-3.5 shadow-md shadow-emerald-200 transition-all active:scale-[0.98]"
            >
              <ShoppingCart size={18} /> Add Box to Cart
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
